import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { t } from 'ttag'
import YumButton from '../YumButton/YumButton'
import dayjs from '../../dayjsConfig'
import { isRtl } from '../../i18nInit'
import YumSmallSelect from '../YumSmallSelect/YumSmallSelect'
import { primaryBlue } from '../../styles/material-ui-theme-v2'
import { useAnnouncer } from '../a11y/useAnnouncer'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    lineHeight: '23px',
    margin: 0,
    justifyContent: 'center',
    '@media (min-width: 992px)': {
      justifyContent: 'flex-end',
    },
  },
}))

export enum ProgressPeriod {
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  //YEAR = 'YEAR',
}

export interface IProgressPeriodProps {
  progressPeriod: ProgressPeriod
  setProgressPeriodDates(progressPeriod: ProgressPeriod, progressPeriodDates: any): any
  fullWidth?: boolean
  // Hide the prev/next period nav buttons.
  hideNav?: boolean
  // Hide the period (Weekly/Monthly) selector.
  hidePeriod?: boolean
}

const options = [
  { key: ProgressPeriod.WEEK, text: t`Weekly` },
  { key: ProgressPeriod.MONTH, text: t`Monthly` },
  //{ key: ProgressPeriod.YEAR, text: t`Yearly` },
]

const getUnit = (progressPeriod: ProgressPeriod) => {
  const units = {
    [ProgressPeriod.WEEK]: 'isoWeek',
    [ProgressPeriod.MONTH]: 'month',
    //[ProgressPeriod.YEAR]: 'year',
  }
  return units[progressPeriod] as dayjs.OpUnitType
}

export default function YumProgressPeriod(props: IProgressPeriodProps) {
  const { progressPeriod, fullWidth, hideNav, hidePeriod } = props
  const classes = useStyles()
  const anchorRef = React.useRef<HTMLButtonElement>(null)
  const [open, setOpen] = React.useState(false)
  //const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const [startDate, setStartDate] = React.useState(new Date())
  const [endDate, setEndDate] = React.useState(new Date())
  const announce = useAnnouncer()

  const announcePeriodRange = (start: Date, end: Date) => {
    const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    announce(t`Showing ${formatter.format(start)} to ${formatter.format(end)}`)
  }

  // Legacy componentDidMount hook equivalent.
  React.useEffect(() => {
    setProgressPeriodDates(progressPeriod)
  }, [])

  const setProgressPeriodDates = (progressPeriod: ProgressPeriod) => {
    const unit = getUnit(progressPeriod)

    // Compute the period in the user's local time zone. Computing it in UTC moved
    // evening users west of UTC into the next day/week (e.g. 9pm Central is
    // already the next UTC day).
    const startDate = dayjs()
      .startOf(unit as any)
      .toDate()
    const endDate = dayjs()
      .endOf(unit as any)
      .endOf('day')
      .toDate()

    // Set to state.
    //setProgressPeriod(progressPeriod)
    setStartDate(startDate)
    setEndDate(endDate)

    // Trigger AJAX call.
    props.setProgressPeriodDates(progressPeriod, { startDate, endDate })
  }

  const setPrevTimePeriod = () => {
    const unit = getUnit(progressPeriod)
    const units = unit === 'isoWeek' ? 'week' : 'month'

    // Prev week.
    const prevStartDate = dayjs(startDate)
      .subtract(1, units)
      .startOf(unit as dayjs.OpUnitType)
      .toDate()
    const prevEndDate = dayjs(endDate).subtract(1, units).endOf(unit).toDate()

    setStartDate(prevStartDate)
    setEndDate(prevEndDate)
    announcePeriodRange(prevStartDate, prevEndDate)

    // Trigger AJAX call.
    props.setProgressPeriodDates(progressPeriod, { startDate: prevStartDate, endDate: prevEndDate })
  }

  const setNextTimePeriod = () => {
    const unit = getUnit(progressPeriod)
    const units = unit === 'isoWeek' ? 'week' : 'month'

    // Prev week.
    const prevStartDate = dayjs(startDate).add(1, units).startOf(unit).toDate()
    const prevEndDate = dayjs(endDate).add(1, units).endOf(unit).toDate()

    setStartDate(prevStartDate)
    setEndDate(prevEndDate)
    announcePeriodRange(prevStartDate, prevEndDate)

    // Trigger AJAX call.
    props.setProgressPeriodDates(progressPeriod, { startDate: prevStartDate, endDate: prevEndDate })
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    }
  }

  const handleMenuItemClick = (event: React.MouseEvent<HTMLElement>, selected: ProgressPeriod) => {
    //setSelected(selected)
    //props.setProgressPeriod(selected, { startDate, endDate })
    setProgressPeriodDates(selected)

    /*if (selected === ProgressPeriod.CUSTOM) {
      setIsDrawerOpen(true)
    }*/

    //setAnchorEl(null)
    handleClose(event)
  }

  // return focus to the button when we transitioned from !open -> open
  /*const prevOpen = React.useRef(open)
  React.useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current!.focus()
    }

    prevOpen.current = open
  }, [open])*/

  /*const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return
    }

    //this.setState({ isDrawerOpen: open })
    setIsDrawerOpen(true)
  }

  const setCustomPeriod = () => () => {
    setIsDrawerOpen(false)
  }*/

  const progressPeriodObj = options.find((o) => o.key === progressPeriod)
  const progressPeriodText = progressPeriodObj ? progressPeriodObj.text : '-'

  // @ts-ignore
  //const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent)

  const handlePeriodChange = (event: React.ChangeEvent<any>) => {
    const { value } = event.target as { value: ProgressPeriod }
    setProgressPeriodDates(value)
  }

  return (
    <div className={classes.root}>
      {!hideNav &&
        (isRtl ? (
          <>
            <YumButton
              onClick={setPrevTimePeriod}
              size="small"
              title={t`Previous period`}
              aria-label={t`Previous period`}
              style={{
                marginLeft: '5px',
                marginRight: '15px',
                paddingLeft: '10px',
                paddingRight: '10px',
              }}
              disabled={startDate <= dayjs.utc('2021-01-01T00:00:00.000Z').toDate()}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M4 2L8 6L4 10" stroke="white" />
              </svg>
            </YumButton>

            <YumButton
              onClick={setNextTimePeriod}
              size="small"
              title={t`Next period`}
              aria-label={t`Next period`}
              style={{
                marginLeft: hidePeriod && !isRtl ? 0 : '15px',
                marginRight: '0',
                paddingLeft: '10px',
                paddingRight: '10px',
              }}
              disabled={endDate > new Date()}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M8 2L4 6L8 10" stroke="white" />
              </svg>
            </YumButton>
          </>
        ) : (
          <>
            <YumButton
              onClick={setPrevTimePeriod}
              size="small"
              title={t`Previous period`}
              aria-label={t`Previous period`}
              style={{
                marginLeft: fullWidth ? 0 : '15px',
                marginRight: '5px',
                paddingLeft: '10px',
                paddingRight: '10px',
              }}
              disabled={startDate <= dayjs.utc('2021-01-01T00:00:00.000Z').toDate()}
            >
              <svg
                style={{ width: '0.75rem', height: '0.75rem' }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M8 2L4 6L8 10" stroke="white" />
              </svg>
            </YumButton>

            <YumButton
              onClick={setNextTimePeriod}
              size="small"
              title={t`Next period`}
              aria-label={t`Next period`}
              style={{
                marginLeft: '0',
                marginRight: hidePeriod && !isRtl ? 0 : '15px',
                paddingLeft: '10px',
                paddingRight: '10px',
              }}
              disabled={endDate > new Date()}
            >
              <svg
                style={{ width: '0.75rem', height: '0.75rem' }}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M4 2L8 6L4 10" stroke="white" />
              </svg>
            </YumButton>
          </>
        ))}

      {!hidePeriod && (
        <YumSmallSelect
          variant="outlined"
          value={progressPeriod}
          onChange={handlePeriodChange}
          inputProps={{ 'aria-label': t`Period`, 'data-a11y-label': t`Period` }}
          fullWidth={fullWidth}
        >
          {options.map((option, index) => (
            <option key={option.key} value={option.key}>
              {option.text}
            </option>
          ))}
        </YumSmallSelect>
      )}
    </div>
  )
}
